import React from 'react';
import FhirSearchService from '@lifeomic/phc-web-toolkit/src/services/FhirSearchService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks';
import useToggle from '@lifeomic/phc-web-toolkit/src/hooks/useToggle';
import { buildAuditEventsQuery } from './queries';
import { FilterTimes } from './types';

const fhirSearchService = new FhirSearchService();

type UseLoadAuditEventsReturnValue = {
  events: fhir.AuditEvent[] | null;
  isLoading: boolean;
  hasMoreToLoad: boolean;
  loadMore: () => Promise<void>;
  resetEvents: () => void;
  makeFetchRequest: () => Promise<void>;
  count: number;
  error: boolean;
};

export const useLoadAuditEvents = (
  searchValue: string,
  filterTimes: FilterTimes,
): UseLoadAuditEventsReturnValue => {
  const [queryProject] = useService(fhirSearchService.search);
  const activeProjectId = useActiveProjectId();

  const [events, setEvents] = React.useState<fhir.AuditEvent[] | null>(null);
  const [searchAfterId, setSearchAfterId] = React.useState<number | null>(-1);
  const [count, setCount] = React.useState<number>(0);
  const [error, setError] = React.useState<boolean>(false);
  const [isLoading, setIsLoading, setLoadingFinished] = useToggle(false);

  const updateCount = React.useRef(0);

  const makeFetchRequest = React.useCallback(async () => {
    updateCount.current++;
    const updateCountBeforeLoad = updateCount.current;

    setIsLoading();
    try {
      const response = await queryProject(
        activeProjectId,
        buildAuditEventsQuery(searchValue, filterTimes),
      );

      if (updateCountBeforeLoad === updateCount.current) {
        const hits = response?.data?.hits;
        setEvents(hits?.hits?.map((_: any) => _._source));
        setSearchAfterId(hits?.hits?.at(-1)?.sort?.[0]);
        if (hits?.total?.value === 10000 && hits?.total?.relation === 'gte')
          setCount(9999);
        else setCount(response?.data?.hits?.total?.value);
      }
    } catch (e) {
      console.error(e);
      setError(true);
    } finally {
      setLoadingFinished();
    }
  }, [
    activeProjectId,
    queryProject,
    setIsLoading,
    setLoadingFinished,
    searchValue,
    filterTimes,
  ]);

  const loadMore = React.useCallback(async () => {
    const updateCountBeforeLoad = updateCount.current;

    setIsLoading();
    try {
      const response = await queryProject(
        activeProjectId,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        buildAuditEventsQuery(searchValue, filterTimes, searchAfterId),
      );

      if (updateCountBeforeLoad === updateCount.current) {
        setEvents(prevResults => [
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          ...prevResults,
          ...response?.data?.hits?.hits?.map((_: any) => _._source),
        ]);
        setSearchAfterId(response?.data?.hits?.hits?.at(-1)?.sort?.[0]);
      }
    } catch (e) {
      console.error(e);
      setError(true);
    } finally {
      setLoadingFinished();
    }
  }, [
    activeProjectId,
    queryProject,
    setIsLoading,
    setLoadingFinished,
    searchValue,
    filterTimes,
    searchAfterId,
  ]);

  const resetEvents = () => {
    setCount(0);
    setLoadingFinished();
    setEvents([]);
    setSearchAfterId(-1);
  };

  React.useEffect(() => {
    if (!activeProjectId) return;

    setEvents([]);
    makeFetchRequest();
  }, [activeProjectId, makeFetchRequest]);

  return {
    events,
    isLoading,
    hasMoreToLoad: !!searchAfterId,
    loadMore,
    resetEvents,
    makeFetchRequest,
    count,
    error,
  };
};
